import React, { useState, useEffect } from 'react';
import { 
  ShareIcon, 
  ChartBarIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { adminSocialAPI } from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage';
import ConnectionsTable from '../../components/admin/ConnectionsTable';
import PlatformHealthDashboard from '../../components/admin/PlatformHealthDashboard';
import UserConnectionsModal from '../../components/admin/UserConnectionsModal';

const SocialConnectionsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState([]);
  const [platformHealth, setPlatformHealth] = useState(null);
  const [platformMetrics, setPlatformMetrics] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [healthStats, setHealthStats] = useState({ healthy: 0, warning: 0, error: 0 });
  const [platformDistribution, setPlatformDistribution] = useState({});

  // Filters
  const [filters, setFilters] = useState({
    platform: '',
    status: '',
    health_status: '',
    user_id: '',
    limit: 50,
    offset: 0
  });

  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);

  // View state
  const [activeTab, setActiveTab] = useState('connections'); // connections, health, metrics
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load connections data
      const connectionsResponse = await adminSocialAPI.getAllConnections(filters);
      const connectionsData = connectionsResponse.data;
      
      setConnections(connectionsData.connections || []);
      setTotalCount(connectionsData.total_count || 0);
      setHealthStats({
        healthy: connectionsData.healthy_count || 0,
        warning: connectionsData.warning_count || 0,
        error: connectionsData.error_count || 0
      });
      setPlatformDistribution(connectionsData.by_platform || {});

      // Load platform health if on health tab
      if (activeTab === 'health') {
        const healthResponse = await adminSocialAPI.getPlatformHealth();
        setPlatformHealth(healthResponse.data);
      }

      // Load metrics if on metrics tab
      if (activeTab === 'metrics') {
        const metricsResponse = await adminSocialAPI.getPlatformMetrics('30d');
        setPlatformMetrics(metricsResponse.data);
      }

    } catch (err) {
      console.error('Error loading social connections data:', err);
      setError('Failed to load social connections data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset pagination when filtering
    }));
  };

  const handleExport = async () => {
    try {
      const response = await adminSocialAPI.exportConnectionsData({
        platform: filters.platform,
        format: 'csv'
      });
      
      // Download the CSV file
      const csvData = atob(response.data.data);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export connections data');
    }
  };

  const handleForceRefresh = async (connectionId) => {
    try {
      await adminSocialAPI.forceTokenRefresh(connectionId);
      // Reload data to show updated status
      await loadData();
    } catch (err) {
      console.error('Error refreshing token:', err);
      setError('Failed to refresh token');
    }
  };

  const openUserModal = (userId, userEmail) => {
    setSelectedUser({ userId, userEmail });
    setUserModalOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'warning':
      case 'rate_limited':
      case 'paused':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
      case 'expired':
      case 'invalid':
      case 'disconnected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Media Connections</h1>
          <p className="text-gray-600">Manage and monitor all user social media connections</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-admin-600 text-white rounded-md text-sm font-medium hover:bg-admin-700"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <ErrorMessage 
          message={error} 
          onClose={() => setError(null)} 
        />
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShareIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Connections</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Healthy</p>
              <p className="text-2xl font-bold text-gray-900">{healthStats.healthy}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Warning</p>
              <p className="text-2xl font-bold text-gray-900">{healthStats.warning}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Errors</p>
              <p className="text-2xl font-bold text-gray-900">{healthStats.error}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Distribution */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {Object.entries(platformDistribution).map(([platform, count]) => (
            <div key={platform} className="text-center">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                <span className="text-2xl">{getPlatformIcon(platform)}</span>
              </div>
              <p className="text-sm text-gray-600 capitalize">{platform}</p>
              <p className="text-lg font-semibold text-gray-900">{count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('connections')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'connections'
                ? 'border-admin-500 text-admin-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Connections
          </button>
          <button
            onClick={() => setActiveTab('health')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'health'
                ? 'border-admin-500 text-admin-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Platform Health
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'metrics'
                ? 'border-admin-500 text-admin-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Metrics
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'connections' && (
        <ConnectionsTable
          connections={connections}
          filters={filters}
          onFilterChange={handleFilterChange}
          onForceRefresh={handleForceRefresh}
          onViewUser={openUserModal}
          totalCount={totalCount}
          loading={loading}
        />
      )}

      {activeTab === 'health' && (
        <PlatformHealthDashboard
          platformHealth={platformHealth}
          loading={loading}
        />
      )}

      {activeTab === 'metrics' && (
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Platform Metrics</h3>
              <p className="text-gray-500">Detailed metrics dashboard coming soon</p>
            </div>
          </div>
        </div>
      )}

      {/* User Connections Modal */}
      {userModalOpen && selectedUser && (
        <UserConnectionsModal
          userId={selectedUser.userId}
          userEmail={selectedUser.userEmail}
          isOpen={userModalOpen}
          onClose={() => {
            setUserModalOpen(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
};

// Helper function to get platform icons
const getPlatformIcon = (platform) => {
  const icons = {
    facebook: '📘',
    instagram: '📷',
    twitter: '🐦',
    linkedin: '💼',
    youtube: '📺',
    tiktok: '🎵',
    threads: '🧵'
  };
  return icons[platform] || '🌐';
};

export default SocialConnectionsAdmin;